// src/pages/AvailableSignups.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, calendarService } from '../firebase/config.js';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query, 
  where,
  addDoc,
  serverTimestamp,
  writeBatch,
  setDoc,
  getDoc,
  deleteDoc
} from 'firebase/firestore';
import { format, isToday, isTomorrow, parseISO, startOfDay, isBefore, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { getScheduleTypeForDate, getPeriodTimesFromSchedule } from '../utils/scheduleUtils.js';
import TenantConfigService from '../services/TenantConfigService.js';
import { 
  createSubstituteJobCalendarEvent, 
  getUserCalendarProvider,
  hasCalendarPermissions as checkCalendarPermissions 
} from '../utils/calendarHelper';

// Calendar Component
const Calendar = ({ availableJobs, onDateClick, selectedDate, schoolHolidays = [] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getJobsForDate = (date) => {
    return availableJobs.filter(job => 
      job.date && isSameDay(job.date, date)
    );
  };

  const isDateHoliday = (date) => {
    if (!schoolHolidays || schoolHolidays.length === 0) return false;
    const checkDate = startOfDay(date);
    return schoolHolidays.some(holiday => {
      if (!holiday.enabled) return false;
      if (holiday.date) {
        try {
          const holidayDate = startOfDay(parseISO(holiday.date));
          return isSameDay(checkDate, holidayDate);
        } catch (e) {
          return false;
        }
      }
      if (holiday.startDate && holiday.endDate) {
        try {
          const startDate = startOfDay(parseISO(holiday.startDate));
          const endDate = startOfDay(parseISO(holiday.endDate));
          return checkDate >= startDate && checkDate <= endDate;
        } catch (e) {
          return false;
        }
      }
      return false;
    });
  };

  const generateCalendarGrid = () => {
    const firstDayOfWeek = getDay(monthStart);
    const grid = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      grid.push(null);
    }
    days.forEach(day => {
      grid.push(day);
    });
    return grid;
  };

  const calendarGrid = generateCalendarGrid();
  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={goToPreviousMonth} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', padding: '8px 12px', cursor: 'pointer' }}>← Previous</button>
        <h3 style={{ margin: 0 }}>{format(currentMonth, 'MMMM yyyy')}</h3>
        <button onClick={goToNextMonth} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', padding: '8px 12px', cursor: 'pointer' }}>Next →</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '5px' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>{day}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
        {calendarGrid.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} style={{ minHeight: '60px', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }} />;
          }
          const jobsForDay = getJobsForDate(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isCurrentDay = isToday(day);
          const isPastDay = isBefore(day, startOfDay(new Date()));
          const isHoliday = isDateHoliday(day);

          return (
            <div
              key={day.toISOString()}
              onClick={() => !isPastDay && !isHoliday && onDateClick(day)}
              style={{
                minHeight: '60px', border: '1px solid #dee2e6', padding: '5px',
                cursor: isPastDay || isHoliday ? 'default' : 'pointer',
                backgroundColor: isPastDay ? '#f8f9fa' : isHoliday ? '#ffebee' : isSelected ? '#e3f2fd' : jobsForDay.length > 0 ? '#fff3cd' : 'white',
                opacity: isPastDay ? 0.5 : 1, position: 'relative'
              }}
            >
              <div style={{ fontWeight: isCurrentDay ? 'bold' : 'normal', color: isCurrentDay ? '#007bff' : isPastDay ? '#6c757d' : 'black' }}>{format(day, 'd')}</div>
              {jobsForDay.length > 0 && (
                <div style={{ fontSize: '10px', backgroundColor: '#dc3545', color: 'white', borderRadius: '10px', padding: '2px 6px', position: 'absolute', top: '2px', right: '2px', minWidth: '16px', textAlign: 'center' }}>{jobsForDay.length}</div>
              )}
              {isHoliday && <div style={{ fontSize: '9px', color: '#d32f2f', marginTop: '2px', fontWeight: 'bold' }}>🏖️ Holiday</div>}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: '15px', fontSize: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '12px', height: '12px', backgroundColor: '#fff3cd', border: '1px solid #dee2e6' }} /><span>Available Jobs</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '12px', height: '12px', backgroundColor: '#e3f2fd', border: '1px solid #dee2e6' }} /><span>Selected Date</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><div style={{ width: '12px', height: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }} /><span>Past/Unavailable</span></div>
        </div>
      </div>
    </div>
  );
};

// Job List Component with multi-select capability
const JobList = ({ 
  jobs, 
  onAcceptClick, 
  showFormId, 
  selectedEntry, 
  substituteToAssign, 
  handleSubstituteDetailsChange, 
  handleSubmit, 
  setShowFormId, 
  canSelectSubstitute, 
  departmentsForSelection, 
  facultyForAdminSelection, 
  confirmationFormRef,
  selectedJobs = [],
  onJobSelectionChange = () => {},
  onMultiAcceptClick = () => {},
  isMultiSelectMode = false,
  setIsMultiSelectMode = () => {},
  hasCalendarPermissions = false,
  calendarPreferences = {},
  mustAcceptFullDay = false,
  isOutsideSub = false,
  mustAcceptFullDayForTenant = () => false,
  allTenantConfigs = {},
  currentUserEmail = null,
  calendarProvider = 'none',
  isCalendarEnabled = false
}) => {
  // Helper to get calendar provider display name
  const getCalendarDisplayName = () => {
    switch (calendarProvider) {
      case 'google': return 'Google Calendar';
      case 'outlook': return 'Outlook Calendar';
      default: return 'Calendar';
    }
  };

  const getCalendarIcon = () => {
    switch (calendarProvider) {
      case 'google': return '📆';
      case 'outlook': return '📧';
      default: return '📅';
    }
  };

  // Mark own requests instead of filtering them out
  const processedJobs = jobs.map(job => {
    const isOwnRequest = currentUserEmail && job.requesterEmail && 
      job.requesterEmail.toLowerCase() === currentUserEmail.toLowerCase();
    return { ...job, isOwnRequest };
  });

  if (!processedJobs || processedJobs.length === 0) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>No jobs available for this date.</div>;
  }

  const groupedJobs = processedJobs.reduce((acc, job) => {
    const key = `${job.tenantId}-${job.name}-${job.date?.toISOString()}-${job.docId}`;
    if (!acc[key]) {
      acc[key] = { teacher: job.name, date: job.date, department: job.department, docId: job.docId, tenantId: job.tenantId, tenantName: job.tenantName, periods: [], isOwnRequest: job.isOwnRequest };
    }
    acc[key].periods.push({ period: job.period, entry: job });
    return acc;
  }, {});

  // Count jobs that are NOT own requests for multi-select display
  const availableJobsCount = processedJobs.filter(job => !job.isOwnRequest).length;

  return (
    <div style={{ marginTop: '20px' }}>
      {isCalendarEnabled && hasCalendarPermissions && calendarPreferences.autoCreateEvents && (
        <div style={{ 
          backgroundColor: calendarProvider === 'google' ? '#e7f5e7' : calendarProvider === 'outlook' ? '#e5f1fb' : '#f8f9fa', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px', 
          border: `1px solid ${calendarProvider === 'google' ? '#4caf50' : calendarProvider === 'outlook' ? '#0078d4' : '#dee2e6'}`, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px' 
        }}>
          <span style={{ fontSize: '20px' }}>{getCalendarIcon()}</span>
          <div><strong>{getCalendarDisplayName()} Integration Active:</strong> Accepted jobs will be automatically added to your {getCalendarDisplayName()} with reminders and location details.</div>
        </div>
      )}
      {isCalendarEnabled && !hasCalendarPermissions && (
        <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffc107', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>{getCalendarIcon()}</span>
          <div><strong>{getCalendarDisplayName()} Integration:</strong> Sign in again to enable automatic {getCalendarDisplayName()} events when accepting jobs.</div>
        </div>
      )}
      {!isCalendarEnabled && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #dee2e6', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>📅</span>
          <div><strong>Calendar Integration:</strong> Your school has not enabled calendar integration. Contact your administrator to set this up.</div>
        </div>
      )}
      {mustAcceptFullDay && (
        <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #ffc107', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div><strong>Full Day Required:</strong> As an outside substitute, you must accept ALL periods shown for each teacher. You cannot select individual periods.</div>
        </div>
      )}
      {availableJobsCount > 1 && !mustAcceptFullDay && (
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={isMultiSelectMode} onChange={(e) => setIsMultiSelectMode(e.target.checked)} style={{ transform: 'scale(1.2)' }} />
            <span style={{ fontWeight: 'bold' }}>Multi-select mode (select multiple periods to accept at once)</span>
          </label>
          {isMultiSelectMode && selectedJobs && selectedJobs.length > 0 && (
            <button onClick={onMultiAcceptClick} style={{ backgroundColor: '#28a745', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9em', marginLeft: 'auto' }}>
              Accept {selectedJobs.length} Selected Job{selectedJobs.length > 1 ? 's' : ''}{isCalendarEnabled && hasCalendarPermissions && calendarPreferences.autoCreateEvents && ` ${getCalendarIcon()}`}
            </button>
          )}
        </div>
      )}

      {Object.entries(groupedJobs).map(([key, group]) => (
        <div key={key} style={{ marginBottom: '20px' }}>
          <div style={{ 
            backgroundColor: group.isOwnRequest ? '#f5f5f5' : '#e9ecef', 
            padding: '10px 15px', 
            borderRadius: '8px 8px 0 0', 
            borderBottom: '2px solid #dee2e6',
            opacity: group.isOwnRequest ? 0.8 : 1
          }}>
            {group.tenantName && (
              <div style={{ display: 'inline-block', backgroundColor: '#007bff', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold', marginBottom: '8px' }}>🏫 {group.tenantName}</div>
            )}
            {group.isOwnRequest && (
              <div style={{ display: 'inline-block', backgroundColor: '#6c757d', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold', marginBottom: '8px', marginLeft: group.tenantName ? '8px' : '0' }}>Your Request</div>
            )}
            <h4 style={{ margin: '0 0 5px 0', color: '#495057' }}>{group.teacher} - {group.department}</h4>
            <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9em' }}>{group.date && !isNaN(group.date.getTime()) ? format(group.date, 'EEEE, MMMM d, yyyy') : 'Invalid Date'}</p>
          </div>
          <div style={{ border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 8px 8px', backgroundColor: 'white' }}>
            {mustAcceptFullDayForTenant(group.tenantId) ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                {/* Check if ALL periods are own requests */}
                {group.periods.every(p => p.entry.isOwnRequest) ? (
                  <div style={{ color: '#6c757d', fontStyle: 'italic', padding: '20px' }}>
                    <p style={{ margin: '0 0 10px 0' }}>⚠️ This is your substitute request</p>
                    <p style={{ margin: 0, fontSize: '0.9em' }}>You cannot sign up for your own request</p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '15px', color: '#495057' }}><strong>Periods Required:</strong> {group.periods.map(p => `Period ${p.period}`).join(', ')}</div>
                    <button
                      onClick={() => {
                        const allPeriodsForTeacher = group.periods.filter(p => !p.entry.isOwnRequest).map(p => p.entry);
                        allPeriodsForTeacher.forEach(job => {
                          if (!selectedJobs.some(j => j.docId === job.docId && j.period === job.period)) {
                            onJobSelectionChange(job, true);
                          }
                        });
                        setTimeout(() => onMultiAcceptClick(), 100);
                      }}
                      style={{ backgroundColor: '#28a745', color: 'white', padding: '12px 30px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1em', fontWeight: 'bold' }}
                    >
                      Accept Full Day (All {group.periods.filter(p => !p.entry.isOwnRequest).length} periods) {isCalendarEnabled && hasCalendarPermissions && calendarPreferences.autoCreateEvents && getCalendarIcon()}
                    </button>
                  </>
                )}
                <details style={{ marginTop: '15px', textAlign: 'left' }}>
                  <summary style={{ cursor: 'pointer', color: '#007bff', fontWeight: 'bold' }}>View Period Details</summary>
                  <div style={{ marginTop: '10px', paddingLeft: '20px' }}>
                    {group.periods.map((periodData, index) => (
                      <div key={index} style={{ 
                        padding: '8px', 
                        borderBottom: index < group.periods.length - 1 ? '1px solid #eee' : 'none',
                        backgroundColor: periodData.entry.isOwnRequest ? '#f5f5f5' : 'transparent',
                        opacity: periodData.entry.isOwnRequest ? 0.7 : 1
                      }}>
                        <strong>Period {periodData.entry.period}</strong>
                        {periodData.entry.room && <span style={{ marginLeft: '10px', color: '#6c757d' }}>Room: {periodData.entry.room}</span>}
                        {periodData.entry.isOwnRequest && <span style={{ marginLeft: '10px', color: '#856404', fontStyle: 'italic', fontSize: '0.85em' }}>⚠️ Your request</span>}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            ) : (
              group.periods.map((periodData, index) => {
                const entry = periodData.entry;
                const fileKey = `${entry.docId}-${entry.period}`;
                const isSelected = selectedJobs && selectedJobs.some(job => job.docId === entry.docId && job.period === entry.period);
                const isOwnRequest = entry.isOwnRequest;

                return (
                  <div key={fileKey} style={{ 
                    padding: '15px 20px', 
                    borderBottom: index < group.periods.length - 1 ? '1px solid #eee' : 'none', 
                    backgroundColor: isOwnRequest ? '#f5f5f5' : isSelected ? '#e8f5e8' : 'white',
                    opacity: isOwnRequest ? 0.7 : 1,
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '15px' 
                  }}>
                    {isMultiSelectMode && !isOwnRequest && (
                      <div><input type="checkbox" checked={isSelected || false} onChange={(e) => onJobSelectionChange(entry, e.target.checked)} style={{ transform: 'scale(1.3)' }} /></div>
                    )}
                    {isMultiSelectMode && isOwnRequest && (
                      <div style={{ width: '20px' }}></div>
                    )}
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Period {entry.period}</p>
                      {entry.room && <p style={{ margin: 0, fontSize: '0.9em', color: '#6c757d' }}>Room: {entry.room}</p>}
                      {isOwnRequest && (
                        <p style={{ margin: '5px 0 0 0', fontSize: '0.85em', color: '#856404', fontStyle: 'italic' }}>
                          ⚠️ This is your request - you cannot sign up for your own substitute request
                        </p>
                      )}
                    </div>
                    {!isMultiSelectMode && !isOwnRequest && (
                      <button onClick={() => onAcceptClick(entry)} style={{ backgroundColor: '#28a745', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9em' }}>
                        Accept Sub Role {isCalendarEnabled && hasCalendarPermissions && calendarPreferences.autoCreateEvents && getCalendarIcon()}
                      </button>
                    )}
                    {!isMultiSelectMode && isOwnRequest && (
                      <span style={{ 
                        backgroundColor: '#e9ecef', 
                        color: '#6c757d', 
                        padding: '8px 16px', 
                        borderRadius: '5px', 
                        fontSize: '0.9em',
                        fontStyle: 'italic'
                      }}>
                        Your Request
                      </span>
                    )}
                    {showFormId === fileKey && (
                      <div ref={confirmationFormRef} style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000, backgroundColor: 'white', padding: '30px', border: '2px solid #ccc', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxWidth: '500px', width: '90%' }}>
                        <h3 style={{ marginTop: '0', marginBottom: '20px' }}>
                          Confirm Substitute Details for Period {entry.period}
                          {isCalendarEnabled && hasCalendarPermissions && calendarPreferences.autoCreateEvents && (<div style={{ fontSize: '0.8em', color: '#28a745', fontWeight: 'normal', marginTop: '5px' }}>{getCalendarIcon()} Will be added to your {getCalendarDisplayName()}</div>)}
                        </h3>
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', marginBottom: '5px' }}><strong>Substitute's Department *</strong></label>
                          {canSelectSubstitute ? (
                            <select name="department" value={substituteToAssign.department} onChange={handleSubstituteDetailsChange} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                              <option value="">Select department of substitute</option>
                              {departmentsForSelection.map(dep => (<option key={dep} value={dep}>{dep}</option>))}
                            </select>
                          ) : (
                            <input type="text" value={substituteToAssign.department} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9ecef' }} />
                          )}
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                          <label style={{ display: 'block', marginBottom: '5px' }}><strong>Substitute's Name *</strong></label>
                          {canSelectSubstitute ? (
                            <select name="name" value={substituteToAssign.name} onChange={handleSubstituteDetailsChange} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} disabled={!substituteToAssign.department || facultyForAdminSelection.length === 0}>
                              <option value="">Select name of substitute</option>
                              {facultyForAdminSelection.map(fac => (<option key={fac.id || fac.email} value={fac.name}>{fac.name}</option>))}
                            </select>
                          ) : (
                            <input type="text" value={substituteToAssign.name} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9ecef' }} />
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button onClick={handleSubmit} style={{ backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1em', flex: 1 }}>Confirm {isCalendarEnabled && hasCalendarPermissions && calendarPreferences.autoCreateEvents && getCalendarIcon()}</button>
                          <button type="button" onClick={() => setShowFormId(null)} style={{ backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '1em', flex: 1 }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ))}
      {showFormId && (<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }} onClick={() => setShowFormId(null)} />)}
    </div>
  );
};

const AvailableSignups = (props) => {
  const { currentUser } = props;
  const navigate = useNavigate();

  const [groupedSubRequests, setGroupedSubRequests] = useState({});
  const [allAvailableJobs, setAllAvailableJobs] = useState([]);
  const [facultyData, setFacultyData] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [substituteToAssign, setSubstituteToAssign] = useState({ department: '', name: '', email: '' });
  const [showFormId, setShowFormId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');
  const [allTenantConfigs, setAllTenantConfigs] = useState({});
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showMultiConfirmForm, setShowMultiConfirmForm] = useState(false);
  const [isCreatingCalendarEvent, setIsCreatingCalendarEvent] = useState(false);
  const [calendarPreferences, setCalendarPreferences] = useState({ autoCreateEvents: true, sendInvitations: true, reminderMinutes: 15 });
  const [subManagementConfig, setSubManagementConfig] = useState({ requireOutsideSubsFullDay: false });
  const [tenantConfig, setTenantConfig] = useState({ scheduleTypes: [], calendarAssignments: { patterns: [], specificDates: [] }, schoolHolidays: [] });
  const [calendarIntegrationConfig, setCalendarIntegrationConfig] = useState({ provider: 'none', enabled: false });

  const confirmationFormRef = useRef(null);
  const canSelectSubstitute = currentUser?.hasElevatedPrivileges || false;
  const hasCalendarPermissions = currentUser?.hasCalendarPermissions || false;
  const isOutsideSub = currentUser?.substituteType === 'outside' || currentUser?.role === 'Outside Sub';
  const isCalendarEnabled = calendarIntegrationConfig.enabled && calendarIntegrationConfig.provider !== 'none';
  const calendarProvider = calendarIntegrationConfig.provider; // 'google', 'outlook', or 'none'

  const mustAcceptFullDayForTenant = useCallback((tenantId) => {
    if (!isOutsideSub) return false;
    const config = allTenantConfigs[tenantId];
    return config?.subManagementConfig?.requireOutsideSubsFullDay || false;
  }, [isOutsideSub, allTenantConfigs]);

  const mustAcceptFullDay = isOutsideSub && subManagementConfig.requireOutsideSubsFullDay;

  const loadCalendarPreferences = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const prefsDoc = await getDoc(doc(db, 'user_preferences', currentUser.uid));
      if (prefsDoc.exists()) {
        const prefs = prefsDoc.data();
        if (prefs.calendar) setCalendarPreferences(prev => ({ ...prev, ...prefs.calendar }));
      }
    } catch (error) {
      console.error('Error loading calendar preferences:', error);
    }
  }, [currentUser?.uid]);

  const saveCalendarPreferences = useCallback(async (newPrefs) => {
    if (!currentUser?.uid) return;
    try {
      await setDoc(doc(db, 'user_preferences', currentUser.uid), { calendar: newPrefs }, { merge: true });
      setCalendarPreferences(newPrefs);
    } catch (error) {
      console.error('Error saving calendar preferences:', error);
    }
  }, [currentUser?.uid]);

  const loadTenantConfigForCalendar = useCallback(async () => {
    if (!currentUser?.tenantId) return;
    try {
      const userTenantIds = currentUser.tenantIds || [currentUser.tenantId];
      const configPromises = userTenantIds.map(async (tenantId) => {
        const [scheduleTypes, calendarAssignments, schoolHolidays, subMgmt, calIntegration] = await Promise.all([
          TenantConfigService.getConfig(tenantId, 'schedule_types', { scheduleTypes: [] }),
          TenantConfigService.getConfig(tenantId, 'calendar_assignments', { patterns: [], specificDates: [] }),
          TenantConfigService.getConfig(tenantId, 'school_holidays', { holidays: [] }),
          TenantConfigService.getConfig(tenantId, 'sub_management_config', { requireOutsideSubsFullDay: false }),
          TenantConfigService.getConfig(tenantId, 'calendar_integration', { provider: 'none', enabled: false })
        ]);
        return {
          tenantId,
          scheduleTypes: scheduleTypes.scheduleTypes || [],
          calendarAssignments: { patterns: calendarAssignments.patterns || [], specificDates: calendarAssignments.specificDates || [] },
          schoolHolidays: schoolHolidays.holidays || [],
          subManagementConfig: subMgmt,
          calendarIntegrationConfig: calIntegration
        };
      });
      const allConfigs = await Promise.all(configPromises);
      const configsByTenant = {};
      allConfigs.forEach(config => { configsByTenant[config.tenantId] = config; });
      const primaryConfig = configsByTenant[currentUser.tenantId];
      if (primaryConfig) {
        setTenantConfig({ scheduleTypes: primaryConfig.scheduleTypes, calendarAssignments: primaryConfig.calendarAssignments, schoolHolidays: primaryConfig.schoolHolidays });
        setSubManagementConfig(primaryConfig.subManagementConfig);
        setCalendarIntegrationConfig(primaryConfig.calendarIntegrationConfig || { provider: 'none', enabled: false });
      }
      setAllTenantConfigs(configsByTenant);
    } catch (error) {
      console.error('Error loading tenant config for calendar:', error);
    }
  }, [currentUser?.tenantId, currentUser?.tenantIds]);

  const getSchoolNameFromTenant = useCallback(async (tenantId) => {
    if (!tenantId) return 'School';
    try {
      const schoolConfig = await TenantConfigService.getConfig(tenantId, 'school_info', {});
      if (schoolConfig.schoolName || schoolConfig.name || schoolConfig.organizationName) {
        return schoolConfig.schoolName || schoolConfig.name || schoolConfig.organizationName;
      }
      if (currentUser?.email) {
        const domain = currentUser.email.split('@')[1];
        if (domain) {
          const domainName = domain.split('.')[0];
          const words = domainName.split(/(?=[A-Z])|[_-]/).filter(word => word.length > 0);
          return words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') || 'School';
        }
      }
      return 'School';
    } catch (error) {
      return 'School';
    }
  }, [currentUser?.email]);

  const getEmergencyContact = useCallback(async (tenantId) => {
    if (!tenantId) return 'School Office';
    try {
      const adminQuery = query(collection(db, 'faculty_staff'), where("tenantId", "==", tenantId), where("role", "in", ["System Administrator", "Educational Administrator", "Principal", "Assistant Principal"]));
      const snapshot = await getDocs(adminQuery);
      if (!snapshot.empty) {
        const admin = snapshot.docs[0].data();
        const contactInfo = `${admin.name} (${admin.email})`;
        return admin.phone ? `${contactInfo} - ${admin.phone}` : contactInfo;
      }
      return 'School Office';
    } catch (error) {
      return 'School Office';
    }
  }, []);

  const createCalendarEventForJob = useCallback(async (jobData, substituteInfo) => {
    
    // Check for any calendar provider (Google OR Outlook)
    const calendarProvider = getUserCalendarProvider(currentUser);
    if (!calendarProvider) {
      console.log('📅 No calendar provider - skipping event creation');
      return null;
    }

    try {
      setIsCreatingCalendarEvent(true);
      
      const dateString = jobData.date.toISOString().split('T')[0];
      const scheduleInfo = getScheduleTypeForDate(dateString, tenantConfig);
      const periodTimes = getPeriodTimesFromSchedule(scheduleInfo, jobData.period);
      
      const [schoolName, emergencyContact] = await Promise.all([
        getSchoolNameFromTenant(jobData.tenantId || currentUser?.tenantId),
        getEmergencyContact(jobData.tenantId || currentUser?.tenantId)
      ]);
      
      const getLessonPlansForPeriod = (jd) => {
        if (jd.periodNotes && jd.periodNotes[jd.period]) return jd.periodNotes[jd.period];
        if (jd.notes) return jd.notes;
        return 'Check with office for lesson plans';
      };
      
      const calendarJobData = {
        id: `${jobData.docId}-${jobData.period}`,
        teacherName: jobData.name,
        subject: jobData.department,
        schoolName: schoolName,
        roomNumber: jobData.room || 'TBD',
        date: jobData.date,
        startTime: periodTimes.startTime,
        endTime: periodTimes.endTime,
        periods: [jobData.period],
        notes: `Substitute job for ${jobData.name} - Period ${jobData.period}`,
        lessonPlans: getLessonPlansForPeriod(jobData),
        teacherEmail: jobData.requesterEmail || 'email not found, contact your System Admin',
        emergencyContact: emergencyContact,
        tenantId: jobData.tenantId || currentUser?.tenantId,
        explicitPeriodTimes: { 
          period: jobData.period, 
          startTime: periodTimes.startTime, 
          endTime: periodTimes.endTime, 
          scheduleType: scheduleInfo?.scheduleType?.name || 'Unknown', 
          source: 'availableSignups-scheduleUtils' 
        },
        reminderMinutes: calendarPreferences.reminderMinutes
      };
      
      // Use unified helper instead of calendarService directly
      console.log(`📅 Creating event via ${calendarProvider}...`);
      return await createSubstituteJobCalendarEvent(currentUser, calendarJobData, substituteInfo);
      
    } catch (error) {
      console.error("Calendar event creation failed:", error);
      return null;
    } finally {
      setIsCreatingCalendarEvent(false);
    }
  }, [currentUser, tenantConfig, getSchoolNameFromTenant, getEmergencyContact, calendarPreferences.reminderMinutes]);

  const createCalendarInvitationForSubstitute = useCallback(async (jobData, substituteInfo) => {
    if (!currentUser?.tenantId) return null;
    try {
      const dateString = jobData.date.toISOString().split('T')[0];
      const scheduleInfo = getScheduleTypeForDate(dateString, tenantConfig);
      const periodTimes = getPeriodTimesFromSchedule(scheduleInfo, jobData.period);
      const [schoolName, emergencyContact] = await Promise.all([
        getSchoolNameFromTenant(jobData.tenantId || currentUser?.tenantId),
        getEmergencyContact(jobData.tenantId || currentUser?.tenantId)
      ]);
      const getLessonPlansForPeriod = (jd) => {
        if (jd.periodNotes && jd.periodNotes[jd.period]) return jd.periodNotes[jd.period];
        if (jd.notes) return jd.notes;
        return 'Check with office for lesson plans';
      };
      const invitationData = {
        type: 'substitute_job_invitation',
        tenantId: jobData.tenantId || currentUser.tenantId,
        jobDetails: {
          id: `${jobData.docId}-${jobData.period}`, teacherName: jobData.name, subject: jobData.department, schoolName: schoolName, roomNumber: jobData.room || 'TBD',
          date: jobData.date, startTime: periodTimes.startTime, endTime: periodTimes.endTime, period: jobData.period, lessonPlans: getLessonPlansForPeriod(jobData),
          teacherEmail: jobData.requesterEmail || 'Contact school office', emergencyContact: emergencyContact, notes: `Substitute assignment for ${jobData.name} - Period ${jobData.period}`, reminderMinutes: calendarPreferences.reminderMinutes
        },
        substituteInfo: { name: substituteInfo.name, email: substituteInfo.email, phone: substituteInfo.phone || 'Contact school office' },
        assignedBy: { name: currentUser.displayName || currentUser.email, email: currentUser.email, role: 'Administrator' },
        createdAt: new Date().toISOString(),
        processed: false
      };
      const notificationDoc = await addDoc(collection(db, 'calendar_invitations'), invitationData);
      return notificationDoc.id;
    } catch (error) {
      console.error("Failed to create calendar invitation:", error);
      return null;
    }
  }, [currentUser, tenantConfig, getSchoolNameFromTenant, getEmergencyContact, calendarPreferences.reminderMinutes]);

  const handleJobSelectionChange = useCallback((job, isSelected) => {
    if (isSelected) {
      setSelectedJobs(prev => [...prev, job]);
    } else {
      setSelectedJobs(prev => prev.filter(j => !(j.docId === job.docId && j.period === job.period)));
    }
  }, []);

  const handleMultiAcceptClick = useCallback(() => {
    if (!selectedJobs || selectedJobs.length === 0) return;
    if (currentUser && !canSelectSubstitute) {
      const selfFacultyData = facultyData.find(f => f.email === currentUser.email);
      setSubstituteToAssign({ department: selfFacultyData?.department || '', name: currentUser.displayName || '', email: currentUser.email || '' });
    } else {
      setSubstituteToAssign({ department: '', name: '', email: '' });
    }
    setShowMultiConfirmForm(true);
  }, [selectedJobs, canSelectSubstitute, facultyData, currentUser]);

  const fetchFaculty = useCallback(async () => {
    if (!currentUser || !currentUser.tenantId) { setFacultyData([]); return; }
    try {
      const facultyQuery = query(collection(db, 'faculty_staff'), where("tenantId", "==", currentUser.tenantId));
      const snapshot = await getDocs(facultyQuery);
      setFacultyData(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching faculty data:", err);
      setFacultyData([]);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && currentUser.tenantId) {
      loadTenantConfigForCalendar();
      fetchFaculty();
    }
  }, [currentUser, loadTenantConfigForCalendar, fetchFaculty]);

  useEffect(() => {
    if (currentUser?.uid) loadCalendarPreferences();
  }, [currentUser, loadCalendarPreferences]);

  const processSubRequestSnapshot = useCallback((snapshot, tenantId, tenantName) => {
    const entries = [];
    const todayForFilter = startOfDay(new Date());
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const docId = docSnap.id;
      const { name, date: dateStringFromDB, department, periods = [], filled = {}, periodNotes = {}, requesterEmail, room, periodRooms = {} } = data;
      let entryDateObject;
      if (dateStringFromDB && typeof dateStringFromDB === 'string' && dateStringFromDB.trim() !== "") {
        try { entryDateObject = parseISO(dateStringFromDB); if (isNaN(entryDateObject.getTime())) entryDateObject = null; } catch (e) { entryDateObject = null; }
      } else { entryDateObject = null; }
      if (!entryDateObject || isBefore(entryDateObject, todayForFilter)) continue;
      for (const period of periods) {
        if (!filled[period]) {
          entries.push({ docId, period, name, date: entryDateObject, originalDateString: dateStringFromDB, department, filled, tenantId, tenantName, periodNotes, requesterEmail, room: periodRooms[period] || room || null });
        }
      }
    }
    return entries;
  }, []);

  const fetchSubRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    if (!currentUser || !currentUser.tenantId) {
      setGroupedSubRequests({});
      setAllAvailableJobs([]);
      setIsLoading(false);
      setError("Could not load opportunities: user or school information missing.");
      return;
    }
    try {
      const userTenantIds = currentUser.tenantIds || [currentUser.tenantId];
      let entries = [];
      if (userTenantIds.length > 1) {
        const queryPromises = userTenantIds.map(async (tenantId) => {
          const tenantQuery = query(collection(db, 'sub_requests'), where("tenantId", "==", tenantId));
          const snapshot = await getDocs(tenantQuery);
          let tenantName = tenantId;
          try {
            const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
            if (tenantDoc.exists()) {
              const tenantData = tenantDoc.data();
              tenantName = tenantData.schoolName || tenantData.name || tenantData.organizationName || tenantId;
            }
          } catch (e) { /* ignore */ }
          return { snapshot, tenantId, tenantName };
        });
        const results = await Promise.all(queryPromises);
        for (const { snapshot, tenantId, tenantName } of results) {
          entries = entries.concat(processSubRequestSnapshot(snapshot, tenantId, tenantName));
        }
      } else {
        const subRequestsQuery = query(collection(db, 'sub_requests'), where("tenantId", "==", currentUser.tenantId));
        const snapshot = await getDocs(subRequestsQuery);
        entries = processSubRequestSnapshot(snapshot, currentUser.tenantId, null);
      }
      entries.sort((a, b) => {
        const aValid = a.date && !isNaN(a.date.getTime());
        const bValid = b.date && !isNaN(b.date.getTime());
        if (aValid && bValid) return a.date - b.date;
        if (aValid) return -1;
        if (bValid) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setAllAvailableJobs(entries);
      const grouped = {};
      for (const entry of entries) {
        let label;
        if (entry.date && !isNaN(entry.date.getTime())) {
          if (isToday(entry.date)) label = `For today, ${format(entry.date, 'MMMM d')}`;
          else if (isTomorrow(entry.date)) label = `For tomorrow, ${format(entry.date, 'MMMM d')}`;
          else label = `For ${format(entry.date, 'MMMM d')}`;
        } else {
          label = "Entries with Invalid/Missing Dates";
        }
        if (!grouped[label]) grouped[label] = [];
        grouped[label].push(entry);
      }
      setGroupedSubRequests(grouped);
    } catch (err) {
      console.error("Error fetching sub requests:", err);
      setError("Could not load available sub requests.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, processSubRequestSnapshot]);

  useEffect(() => {
    if (currentUser && currentUser.tenantId) fetchSubRequests();
    else { setGroupedSubRequests({}); setAllAvailableJobs([]); setIsLoading(false); }
  }, [currentUser, fetchSubRequests, showFormId]);

  useEffect(() => {
    if (showFormId && confirmationFormRef.current) {
      confirmationFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showFormId]);

  useEffect(() => {
    if (!isMultiSelectMode) setSelectedJobs([]);
  }, [isMultiSelectMode]);

  useEffect(() => {
    const initializeCalendarForUser = async () => {
      if (!currentUser) return;
      
      const provider = getUserCalendarProvider(currentUser);
      
      if (provider === 'google' && calendarService) {
        try {
          calendarService.setCurrentUser(auth.currentUser);
          if (!calendarService.isInitialized) await calendarService.initializeGoogleAPIs();
        } catch (error) {
          console.error("Failed to initialize Google calendar service:", error);
        }
      }
      // Note: Outlook is initialized during sign-in via signInWithMicrosoft()
    };
    initializeCalendarForUser();
  }, [currentUser]);

  const getJobsForSelectedDate = useCallback(() => {
    if (!selectedDate) return [];
    return allAvailableJobs.filter(job => job.date && isSameDay(job.date, selectedDate));
  }, [selectedDate, allAvailableJobs]);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setViewMode('list');
    setSelectedJobs([]);
    setIsMultiSelectMode(false);
  };

  const handleSubmit = async () => {
    if (!selectedEntry || !substituteToAssign.name || !substituteToAssign.email) {
      alert('Please ensure the substitute has a name and email address.');
      return;
    }
    // Prevent accepting your own sub request
    if (selectedEntry.requesterEmail && selectedEntry.requesterEmail.toLowerCase() === currentUser?.email?.toLowerCase()) {
      alert('You cannot accept your own substitute request.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const provider = getUserCalendarProvider(currentUser);
      if (provider) {
        await createCalendarEventForJob(selectedEntry, { name: substituteToAssign.name, email: substituteToAssign.email });
      }
      await updateDoc(doc(db, "sub_requests", selectedEntry.docId), {
        [`filled.${selectedEntry.period}`]: { name: substituteToAssign.name, email: substituteToAssign.email, uid: currentUser.uid },
        lastModified: new Date(),
        modifiedBy: currentUser.uid
      });
      const subJobData = {
        requestId: selectedEntry.docId, tenantId: selectedEntry.tenantId, substituteName: substituteToAssign.name, substituteEmail: substituteToAssign.email,
        substituteType: currentUser.substituteType || 'outside', teacherName: selectedEntry.name, subject: selectedEntry.department,
        schoolName: selectedEntry.schoolName || selectedEntry.tenantName || 'School', roomNumber: selectedEntry.room || 'TBD',
        date: selectedEntry.date, period: selectedEntry.period, status: 'confirmed', createdAt: new Date(), createdBy: currentUser.uid, assignedBy: currentUser.uid, assignedAt: new Date()
      };
      if (selectedEntry.startTime) subJobData.startTime = selectedEntry.startTime;
      if (selectedEntry.endTime) subJobData.endTime = selectedEntry.endTime;
      await addDoc(collection(db, "sub_jobs"), subJobData);
      alert("SUCCESS! Sub role accepted.");
      setSelectedEntry(null);
      setSubstituteToAssign({ department: '', name: '', email: '' });
      setShowFormId(null);
      setIsLoading(false);
      await fetchSubRequests();
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setError(`Error: ${error.message}`);
      setIsLoading(false);
    }
  };

  const handleMultiSubmit = async () => {
    if (!currentUser) { alert("You must be logged in."); return; }
    if (!substituteToAssign.name || !substituteToAssign.department || !substituteToAssign.email) { alert('Please ensure the substitute details are filled.'); return; }
    if (!canSelectSubstitute && substituteToAssign.email !== currentUser.email) { alert("You can only accept sub roles for yourself."); return; }
    if (!selectedJobs || selectedJobs.length === 0) { alert("No jobs selected."); return; }

    // Filter out own requests
    const validJobs = selectedJobs.filter(job => {
      const isOwnRequest = job.requesterEmail && job.requesterEmail.toLowerCase() === currentUser?.email?.toLowerCase();
      return !isOwnRequest;
    });
    if (validJobs.length === 0) { alert("You cannot accept your own substitute requests."); return; }
    if (validJobs.length !== selectedJobs.length) {
      alert(`Note: ${selectedJobs.length - validJobs.length} of your own request(s) were excluded.`);
    }

    try {
      const batch = writeBatch(db);
      const substituteFacultyInfo = facultyData.find(f => f.email === substituteToAssign.email);
      const isAdminAcceptingForOther = canSelectSubstitute && substituteToAssign.email !== currentUser.email;
      const jobsByDoc = {};
      validJobs.forEach(job => {
        if (!jobsByDoc[job.docId]) jobsByDoc[job.docId] = [];
        jobsByDoc[job.docId].push(job);
      });
      for (const [docId, jobs] of Object.entries(jobsByDoc)) {
        const updateData = {};
        jobs.forEach(job => {
          updateData[`filled.${job.period}`] = { name: substituteToAssign.name, email: substituteToAssign.email, uid: substituteFacultyInfo?.id || (substituteToAssign.email === currentUser.email ? currentUser.uid : null) };
        });
        batch.update(doc(db, 'sub_requests', docId), updateData);
      }
      await batch.commit();

      let calendarEventsCreated = 0, calendarInvitationsSent = 0;
      setIsCreatingCalendarEvent(true);
      const substituteInfo = { name: substituteToAssign.name, email: substituteToAssign.email, phone: substituteFacultyInfo?.phone || currentUser?.phone || 'Contact school office' };

      if (isAdminAcceptingForOther && calendarPreferences.sendInvitations) {
        for (const job of validJobs) {
          const invId = await createCalendarInvitationForSubstitute(job, substituteInfo);
          if (invId) calendarInvitationsSent++;
        }
      } else if (getUserCalendarProvider(currentUser) && substituteToAssign.email === currentUser.email && calendarPreferences.autoCreateEvents) {
        for (const job of validJobs) {
          const evId = await createCalendarEventForJob(job, substituteInfo);
          if (evId) calendarEventsCreated++;
        }
      }
      setIsCreatingCalendarEvent(false);

      if (currentUser.substituteType === 'outside' || substituteFacultyInfo?.role === 'Outside Sub') {
        for (const job of validJobs) {
          const subJobData = {
            requestId: job.docId, tenantId: job.tenantId, substituteName: substituteToAssign.name, substituteEmail: substituteToAssign.email,
            substituteType: currentUser.substituteType || 'outside', teacherName: job.name, subject: job.department,
            schoolName: job.schoolName || job.tenantName || 'School', roomNumber: job.room || 'TBD',
            date: job.date, period: job.period, status: 'confirmed', createdAt: new Date(), createdBy: currentUser.uid, assignedBy: currentUser.uid, assignedAt: new Date()
          };
          if (job.startTime) subJobData.startTime = job.startTime;
          if (job.endTime) subJobData.endTime = job.endTime;
          await addDoc(collection(db, 'sub_jobs'), subJobData);
        }
      }

      setShowMultiConfirmForm(false);
      setSelectedJobs([]);
      setIsMultiSelectMode(false);
      setSubstituteToAssign({ department: '', name: '', email: '' });

      let successMessage = `Successfully accepted ${validJobs.length} substitute role${validJobs.length > 1 ? 's' : ''}!`;
      if (calendarEventsCreated > 0) successMessage += ` 📅 ${calendarEventsCreated} event(s) added to your calendar.`;
      else if (calendarInvitationsSent > 0) successMessage += ` 📧 ${calendarInvitationsSent} calendar invitation(s) sent.`;
      alert(successMessage);
      await fetchSubRequests();

      if (validJobs.length > 0) {
        setTimeout(() => navigate(`/accepted-job/${validJobs[0].docId}/${validJobs[0].period}`), 1000);
      }
    } catch (err) {
      console.error("ERROR in handleMultiSubmit:", err);
      alert('Failed to accept sub roles. Please try again.');
    } finally {
      setIsCreatingCalendarEvent(false);
    }
  };

  const departmentsForSelection = [...new Set(facultyData.map(f => f.department).filter(Boolean))].sort();
  const facultyForAdminSelection = facultyData.filter(f => f.department === substituteToAssign.department).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const handleAcceptClick = (entry) => {
    // Prevent accepting your own sub request
    if (entry.requesterEmail && entry.requesterEmail.toLowerCase() === currentUser?.email?.toLowerCase()) {
      alert('You cannot accept your own substitute request.');
      return;
    }
    setSelectedEntry(entry);
    if (currentUser && !canSelectSubstitute) {
      const selfFacultyData = facultyData.find(f => f.email === currentUser.email);
      setSubstituteToAssign({ department: selfFacultyData?.department || '', name: currentUser.displayName || '', email: currentUser.email || '' });
    } else {
      setSubstituteToAssign({ department: '', name: '', email: '' });
    }
    setShowFormId(`${entry.docId}-${entry.period}`);
  };

  const handleSubstituteDetailsChange = (e) => {
    const { name, value } = e.target;
    if (name === "department") {
      setSubstituteToAssign({ department: value, name: '', email: '' });
    } else if (name === "name") {
      const selectedFaculty = facultyData.find(f => f.name === value && f.department === substituteToAssign.department);
      setSubstituteToAssign(prev => ({ ...prev, name: value, email: selectedFaculty?.email || '' }));
    }
  };

  const renderCalendarSettings = () => (
    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
      <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>📅 Calendar Integration Settings</h4>
      {hasCalendarPermissions && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={calendarPreferences.autoCreateEvents} onChange={(e) => saveCalendarPreferences({ ...calendarPreferences, autoCreateEvents: e.target.checked })} style={{ transform: 'scale(1.2)' }} />
            <span>Automatically add accepted jobs to my calendar</span>
          </label>
        </div>
      )}
      {canSelectSubstitute && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={calendarPreferences.sendInvitations} onChange={(e) => saveCalendarPreferences({ ...calendarPreferences, sendInvitations: e.target.checked })} style={{ transform: 'scale(1.2)' }} />
            <span>Send calendar invitations when assigning jobs to other substitutes</span>
          </label>
        </div>
      )}
      {(hasCalendarPermissions || canSelectSubstitute) && (
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Default reminder time:</label>
          <select value={calendarPreferences.reminderMinutes} onChange={(e) => saveCalendarPreferences({ ...calendarPreferences, reminderMinutes: parseInt(e.target.value) })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}>
            <option value={0}>No reminder</option>
            <option value={5}>5 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
          </select>
        </div>
      )}
      {!hasCalendarPermissions && !canSelectSubstitute && (<div style={{ color: '#6c757d', fontStyle: 'italic' }}>Calendar integration not available for your account level.</div>)}
    </div>
  );

  if (isLoading && Object.keys(groupedSubRequests).length === 0) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading available sub opportunities...</div>;
  }
  if (error) {
    return <div style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 15px' }}>
      <h1>Available Sub Opportunities for {currentUser?.displayName || currentUser?.name || 'User'}</h1>
      {currentUser?.tenantIds && currentUser.tenantIds.length > 1 && (
        <div style={{ backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #2196f3' }}>
          <strong>🏫 Multi-School Access:</strong> You have access to jobs from {currentUser.tenantIds.length} schools. Jobs are clearly labeled with the school name.
        </div>
      )}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button onClick={() => setViewMode('calendar')} style={{ backgroundColor: viewMode === 'calendar' ? '#007bff' : '#6c757d', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>📅 Calendar View</button>
        <button onClick={() => setViewMode('list')} style={{ backgroundColor: viewMode === 'list' ? '#007bff' : '#6c757d', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>📋 List View</button>
      </div>
      {renderCalendarSettings()}
      {isCreatingCalendarEvent && (
        <div style={{ backgroundColor: '#d4edda', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c3e6cb', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '20px', height: '20px', border: '2px solid #28a745', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span><strong>Creating calendar event...</strong> Please wait.</span>
        </div>
      )}
      {Object.keys(groupedSubRequests).length === 0 && !isLoading ? (
        <p>No sub opportunities currently available for your school.</p>
      ) : (
        <>
          {viewMode === 'calendar' ? (
            <div>
              <Calendar availableJobs={allAvailableJobs} onDateClick={handleDateClick} selectedDate={selectedDate} schoolHolidays={tenantConfig.schoolHolidays} />
              {selectedDate && (
                <div style={{ marginTop: '20px' }}>
                  <h2 style={{ borderBottom: '2px solid #007bff', paddingBottom: '10px', color: '#007bff' }}>Jobs for {format(selectedDate, 'EEEE, MMMM d, yyyy')}</h2>
                  <JobList jobs={getJobsForSelectedDate()} onAcceptClick={handleAcceptClick} showFormId={showFormId} selectedEntry={selectedEntry} substituteToAssign={substituteToAssign} handleSubstituteDetailsChange={handleSubstituteDetailsChange} handleSubmit={handleSubmit} setShowFormId={setShowFormId} canSelectSubstitute={canSelectSubstitute} departmentsForSelection={departmentsForSelection} facultyForAdminSelection={facultyForAdminSelection} confirmationFormRef={confirmationFormRef} selectedJobs={selectedJobs} onJobSelectionChange={handleJobSelectionChange} onMultiAcceptClick={handleMultiAcceptClick} isMultiSelectMode={isMultiSelectMode} setIsMultiSelectMode={setIsMultiSelectMode} hasCalendarPermissions={hasCalendarPermissions} calendarPreferences={calendarPreferences} mustAcceptFullDay={mustAcceptFullDay} isOutsideSub={isOutsideSub} mustAcceptFullDayForTenant={mustAcceptFullDayForTenant} allTenantConfigs={allTenantConfigs} currentUserEmail={currentUser?.email} calendarProvider={calendarIntegrationConfig.provider} isCalendarEnabled={calendarIntegrationConfig.enabled && calendarIntegrationConfig.provider !== 'none'} />
                </div>
              )}
            </div>
          ) : (
            <div>
              {selectedDate && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Showing jobs for: <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong></span>
                  <button onClick={() => setSelectedDate(null)} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', padding: '5px 10px', cursor: 'pointer' }}>Clear Filter</button>
                </div>
              )}
              {selectedDate ? (
                <JobList jobs={getJobsForSelectedDate()} onAcceptClick={handleAcceptClick} showFormId={showFormId} selectedEntry={selectedEntry} substituteToAssign={substituteToAssign} handleSubstituteDetailsChange={handleSubstituteDetailsChange} handleSubmit={handleSubmit} setShowFormId={setShowFormId} canSelectSubstitute={canSelectSubstitute} departmentsForSelection={departmentsForSelection} facultyForAdminSelection={facultyForAdminSelection} confirmationFormRef={confirmationFormRef} selectedJobs={selectedJobs} onJobSelectionChange={handleJobSelectionChange} onMultiAcceptClick={handleMultiAcceptClick} isMultiSelectMode={isMultiSelectMode} setIsMultiSelectMode={setIsMultiSelectMode} hasCalendarPermissions={hasCalendarPermissions} calendarPreferences={calendarPreferences} mustAcceptFullDay={mustAcceptFullDay} isOutsideSub={isOutsideSub} mustAcceptFullDayForTenant={mustAcceptFullDayForTenant} allTenantConfigs={allTenantConfigs} currentUserEmail={currentUser?.email} calendarProvider={calendarIntegrationConfig.provider} isCalendarEnabled={calendarIntegrationConfig.enabled && calendarIntegrationConfig.provider !== 'none'} />
              ) : (
                Object.entries(groupedSubRequests).map(([label, entriesInGroup]) => (
                  <div key={label} style={{ marginBottom: '30px' }}>
                    <h2 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>{label}</h2>
                    {entriesInGroup.length === 0 ? (<p>No opportunities for this date group.</p>) : (
                      <JobList jobs={entriesInGroup} onAcceptClick={handleAcceptClick} showFormId={showFormId} selectedEntry={selectedEntry} substituteToAssign={substituteToAssign} handleSubstituteDetailsChange={handleSubstituteDetailsChange} handleSubmit={handleSubmit} setShowFormId={setShowFormId} canSelectSubstitute={canSelectSubstitute} departmentsForSelection={departmentsForSelection} facultyForAdminSelection={facultyForAdminSelection} confirmationFormRef={confirmationFormRef} selectedJobs={selectedJobs} onJobSelectionChange={handleJobSelectionChange} onMultiAcceptClick={handleMultiAcceptClick} isMultiSelectMode={isMultiSelectMode} setIsMultiSelectMode={setIsMultiSelectMode} hasCalendarPermissions={hasCalendarPermissions} calendarPreferences={calendarPreferences} mustAcceptFullDay={mustAcceptFullDay} isOutsideSub={isOutsideSub} mustAcceptFullDayForTenant={mustAcceptFullDayForTenant} allTenantConfigs={allTenantConfigs} currentUserEmail={currentUser?.email} calendarProvider={calendarIntegrationConfig.provider} isCalendarEnabled={calendarIntegrationConfig.enabled && calendarIntegrationConfig.provider !== 'none'} />
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
      {showMultiConfirmForm && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }} onClick={() => setShowMultiConfirmForm(false)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000, backgroundColor: 'white', padding: '30px', border: '2px solid #ccc', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: '0', marginBottom: '20px' }}>
              Confirm Substitute Details for {selectedJobs ? selectedJobs.length : 0} Job{selectedJobs && selectedJobs.length > 1 ? 's' : ''}
              {hasCalendarPermissions && calendarPreferences.autoCreateEvents && (<div style={{ fontSize: '0.8em', color: '#28a745', fontWeight: 'normal', marginTop: '5px' }}>📅 Will be added to your Google Calendar</div>)}
              {canSelectSubstitute && calendarPreferences.sendInvitations && (<div style={{ fontSize: '0.8em', color: '#17a2b8', fontWeight: 'normal', marginTop: '5px' }}>📧 Calendar invitations will be sent to substitute</div>)}
            </h3>
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1em' }}>Selected Jobs:</h4>
              {selectedJobs && selectedJobs.map((job) => (
                <div key={`${job.docId}-${job.period}`} style={{ fontSize: '0.9em', marginBottom: '5px', padding: '5px', backgroundColor: 'white', borderRadius: '4px' }}>
                  {job.tenantName && <span style={{ color: '#007bff', fontWeight: 'bold' }}>🏫 {job.tenantName} - </span>}
                  {job.name} - {job.department} - Period {job.period}
                  {job.date && !isNaN(job.date.getTime()) && (<span style={{ color: '#6c757d', marginLeft: '10px' }}>({format(job.date, 'MMM d')})</span>)}
                </div>
              ))}
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}><strong>Substitute's Department *</strong></label>
              {canSelectSubstitute ? (
                <select name="department" value={substituteToAssign.department} onChange={handleSubstituteDetailsChange} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                  <option value="">Select department of substitute</option>
                  {departmentsForSelection.map(dep => (<option key={dep} value={dep}>{dep}</option>))}
                </select>
              ) : (<input type="text" value={substituteToAssign.department} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9ecef' }} />)}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}><strong>Substitute's Name *</strong></label>
              {canSelectSubstitute ? (
                <select name="name" value={substituteToAssign.name} onChange={handleSubstituteDetailsChange} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} disabled={!substituteToAssign.department || facultyForAdminSelection.length === 0}>
                  <option value="">Select name of substitute</option>
                  {facultyForAdminSelection.map(fac => (<option key={fac.id || fac.email} value={fac.name}>{fac.name}</option>))}
                </select>
              ) : (<input type="text" value={substituteToAssign.name} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9ecef' }} />)}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleMultiSubmit} disabled={isCreatingCalendarEvent} style={{ backgroundColor: isCreatingCalendarEvent ? '#6c757d' : '#28a745', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '5px', cursor: isCreatingCalendarEvent ? 'not-allowed' : 'pointer', fontSize: '1em', flex: 1 }}>
                {isCreatingCalendarEvent ? 'Creating Calendar Events...' : `Confirm All ${selectedJobs ? selectedJobs.length : 0} Job${selectedJobs && selectedJobs.length > 1 ? 's' : ''}${hasCalendarPermissions && calendarPreferences.autoCreateEvents ? ' 📅' : ''}${canSelectSubstitute && calendarPreferences.sendInvitations ? ' 📧' : ''}`}
              </button>
              <button type="button" onClick={() => setShowMultiConfirmForm(false)} disabled={isCreatingCalendarEvent} style={{ backgroundColor: '#dc3545', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '5px', cursor: isCreatingCalendarEvent ? 'not-allowed' : 'pointer', fontSize: '1em', flex: 1 }}>Cancel</button>
            </div>
          </div>
        </>
      )}
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AvailableSignups;